import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

declare global {
  interface Window {
    anychart: any
  }
}

export function CoffeeFlavorWheel() {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<any>(null)
  const isInitialized = useRef(false)
  const titleRef = useRef<string>(t("charts.flavor_wheel.title"))

  useEffect(() => {
    titleRef.current = t("charts.flavor_wheel.title")
  }, [t])

  useEffect(() => {
    // Prevent multiple initializations
    if (isInitialized.current) return

    // Check if scripts are already loaded
    if (window.anychart) {
      initChart()
      return
    }

    // Load AnyChart scripts
    const scripts = [
      "https://cdn.anychart.com/releases/v8/js/anychart-base.min.js",
      "https://cdn.anychart.com/releases/v8/js/anychart-sunburst.min.js",
      "https://cdn.anychart.com/releases/v8/js/anychart-ui.min.js",
      "https://cdn.anychart.com/releases/v8/js/anychart-exports.min.js",
      "https://cdn.anychart.com/releases/v8/js/anychart-data-adapter.min.js",
    ]

    const links = [
      "https://cdn.anychart.com/releases/v8/css/anychart-ui.min.css",
      "https://cdn.anychart.com/releases/v8/fonts/css/anychart-font.min.css",
    ]

    // Load CSS files
    links.forEach((href) => {
      const link = document.createElement("link")
      link.rel = "stylesheet"
      link.href = href
      document.head.appendChild(link)
    })

    // Load scripts sequentially
    const loadScripts = async () => {
      for (const src of scripts) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement("script")
          script.src = src
          script.onload = () => resolve()
          script.onerror = () =>
            reject(new Error(`Failed to load script: ${src}`))
          document.head.appendChild(script)
        })
      }
      initChart()
    }

    loadScripts().catch((error) => {
      console.error("Error loading AnyChart scripts:", error)
    })

    return () => {
      if (chartRef.current) {
        chartRef.current.dispose()
        chartRef.current = null
      }
      isInitialized.current = false
    }
  }, [])

  const initChart = () => {
    if (!window.anychart || !containerRef.current || isInitialized.current)
      return

    isInitialized.current = true

    // Load the coffee flavour wheel data
    window.anychart.data.loadJsonFile(
      "https://cdn.anychart.com/samples/sunburst-charts/coffee-flavour-wheel/data.json",
      (data: any) => {
        // Dispose of any existing chart
        if (chartRef.current) {
          chartRef.current.dispose()
        }

        // Make tree from the data
        const dataTree = window.anychart.data.tree(data, "as-table")

        // Create sunburst chart
        const chart = window.anychart.sunburst(dataTree)

        // Set calculation mode
        chart.calculationMode("ordinal-from-root")

        // Set chart title
        chart.title(titleRef.current)

        // Set settings for the penultimate level labels
        chart.level(-2).labels().position("radial")

        // Set chart labels settings
        chart.labels().hAlign("center")

        // Set settings for leaves labels
        chart.leaves().labels().minFontSize(8).textOverflow("...")

        // The fill specified in the data has priority
        // Set point fill
        chart.fill(function (this: any) {
          return window.anychart.color.darken(this.parentColor, 0.15)
        })

        // Set container for the chart
        chart.container(containerRef.current)

        // Initiate chart drawing
        chart.draw()

        // Store chart reference for cleanup
        chartRef.current = chart
      },
    )
  }

  return <div ref={containerRef} className="h-full w-full" />
}
